import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Zoom } from '@mui/material';
import { Trash2, X } from 'lucide-react';
import Swal from 'sweetalert2';

/**
 * Reusable Custom Card Confirmation Modal Component
 */
export const ConfirmModal = ({
    open,
    onClose,
    onConfirm,
    title = "Confirm Action",
    text = "Are you sure you want to perform this action? This process cannot be undone.",
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmBtnClass = "color-config text-white",
    requireRemark = false,
    remarkPlaceholder = "Enter remark...",
    icon: Icon = Trash2,
    iconBgColor = "#fee2e2",
    iconColor = "#dc2626"
}) => {
    const [remark, setRemark] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (open) {
            setRemark('');
            setErrorMessage('');
        }
    }, [open]);

    const handleConfirmClick = () => {
        if (requireRemark && !remark.trim()) {
            setErrorMessage("Remark is required");
            return;
        }
        setErrorMessage('');
        onConfirm(remark);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Zoom}
            transitionDuration={250}
            PaperProps={{
                className: "dark:!bg-[#161f30] dark:!text-gray-100",
                sx: {
                    borderRadius: '16px',
                    maxWidth: '420px',
                    width: '100%',
                    p: 1,
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ padding: '8px', backgroundColor: iconBgColor, borderRadius: '50%', color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={20} />
                    </div>
                    <span className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</span>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer"
                >
                    <X size={18} />
                </button>
            </DialogTitle>
            <DialogContent sx={{ pt: 1, pb: 2 }}>
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">{text}</p>
                {requireRemark && (
                    <div className="mt-2">
                        <textarea
                            value={remark}
                            onChange={(e) => {
                                setRemark(e.target.value);
                                if (e.target.value.trim()) setErrorMessage('');
                            }}
                            placeholder={remarkPlaceholder}
                            rows={3}
                            className={`w-full p-2.5 text-xs rounded-lg border bg-gray-50 dark:bg-slate-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 transition-all ${
                                errorMessage ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-slate-700 focus:ring-blue-500'
                            }`}
                        />
                        {errorMessage && (
                            <span className="text-[11px] text-red-500 mt-1 block font-medium">{errorMessage}</span>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions sx={{ gap: 1, pt: 0, px: 2, pb: 1.5 }}>
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                    {cancelText}
                </button>
                <button
                    type="button"
                    onClick={handleConfirmClick}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold shadow-xs transition-all flex items-center gap-1.5 cursor-pointer ${confirmBtnClass}`}
                >
                    <Icon size={14} />
                    {confirmText}
                </button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Common Promise-based Confirm Dialog Utility Function
 * Switches between SweetAlert (Swal) and Custom Card Modal depending on Master Settings ('deleteConfirmStyle').
 */
export const showConfirmDialog = async ({
    title = "Are you sure?",
    text = "Do you want to perform this action?",
    icon = "warning",
    confirmText = "Confirm",
    cancelText = "Cancel",
    requireRemark = false,
    remarkPlaceholder = "Enter remark...",
    confirmBtnClass = "color-config text-white px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer shadow-xs flex items-center gap-1.5",
    cancelBtnClass = "bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-700 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer"
}) => {
    const deleteConfirmStyle = localStorage.getItem('deleteConfirmStyle') || 'swal';
    const isCustomCard = deleteConfirmStyle === 'customCard' || deleteConfirmStyle === 'advanced';

    if (isCustomCard) {
        return new Promise((resolve) => {
            if (typeof document !== 'undefined' && !document.getElementById('custom-card-anim-style')) {
                const style = document.createElement('style');
                style.id = 'custom-card-anim-style';
                style.innerHTML = `
                    @keyframes customModalZoomIn {
                        0% { opacity: 0; transform: scale(0.85) translateY(12px); }
                        100% { opacity: 1; transform: scale(1) translateY(0); }
                    }
                    @keyframes customModalFadeIn {
                        0% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    .animate-modalZoom { animation: customModalZoomIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                    .animate-fadeIn { animation: customModalFadeIn 0.15s ease-out forwards; }
                `;
                document.head.appendChild(style);
            }

            const container = document.createElement('div');
            container.id = 'custom-confirm-modal-container';
            container.className = 'fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 select-none animate-fadeIn';

            container.innerHTML = `
                <div class="relative w-full max-w-[420px] bg-white dark:bg-[#161f30] text-gray-900 dark:text-gray-100 rounded-2xl p-6 border border-gray-200/90 dark:border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.18)] animate-modalZoom pointer-events-auto">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-3">
                            <div class="p-2.5 bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                            </div>
                            <h5 class="text-base font-bold text-gray-800 dark:text-gray-100 m-0 tracking-tight">${title}</h5>
                        </div>
                        <button type="button" id="custom-modal-close-x" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer outline-none shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                    <p class="text-xs text-gray-600 dark:text-gray-300 mt-2 mb-3.5 text-left leading-relaxed">${text}</p>
                    ${requireRemark ? `
                        <div class="mt-2 mb-4 text-left">
                            <textarea id="custom-modal-remark-input" class="w-full p-3 text-xs rounded-xl border border-gray-300 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500 transition-all" placeholder="${remarkPlaceholder}" rows="3"></textarea>
                            <div id="custom-modal-remark-error" class="text-[11px] text-red-500 mt-1.5 hidden font-medium flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                Remark is required
                            </div>
                        </div>
                    ` : ''}
                    <div class="flex items-center justify-end gap-2 mt-4 pt-1">
                        <button type="button" id="custom-modal-cancel-btn" class="${cancelBtnClass}">${cancelText}</button>
                        <button type="button" id="custom-modal-confirm-btn" class="${confirmBtnClass.replace('me-2', '')}">${confirmText}</button>
                    </div>
                </div>
            `;

            document.body.appendChild(container);

            let cleanedUp = false;
            const cleanupAndResolve = (result) => {
                if (!cleanedUp) {
                    cleanedUp = true;
                    window.removeEventListener('keydown', handleKeyDown);
                    if (container && container.parentNode) {
                        container.parentNode.removeChild(container);
                    }
                    resolve(result);
                }
            };

            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    cleanupAndResolve({ isConfirmed: false, value: null });
                }
            };
            window.addEventListener('keydown', handleKeyDown);

            container.onclick = (e) => {
                if (e.target === container) {
                    cleanupAndResolve({ isConfirmed: false, value: null });
                }
            };

            const closeX = container.querySelector('#custom-modal-close-x');
            if (closeX) {
                closeX.onclick = (e) => {
                    e.stopPropagation();
                    cleanupAndResolve({ isConfirmed: false, value: null });
                };
            }

            const cancelBtn = container.querySelector('#custom-modal-cancel-btn');
            if (cancelBtn) {
                cancelBtn.onclick = (e) => {
                    e.stopPropagation();
                    cleanupAndResolve({ isConfirmed: false, value: null });
                };
            }

            const confirmBtn = container.querySelector('#custom-modal-confirm-btn');
            if (confirmBtn) {
                confirmBtn.onclick = (e) => {
                    e.stopPropagation();
                    window.__isDeleteModalActive = true;
                    setTimeout(() => { window.__isDeleteModalActive = false; }, 3000);
                    if (requireRemark) {
                        const remarkInput = container.querySelector('#custom-modal-remark-input');
                        const remarkVal = remarkInput ? remarkInput.value.trim() : '';
                        if (!remarkVal) {
                            const errEl = container.querySelector('#custom-modal-remark-error');
                            if (errEl) errEl.classList.remove('hidden');
                            if (remarkInput) remarkInput.focus();
                            return;
                        }
                        cleanupAndResolve({ isConfirmed: true, value: remarkVal });
                    } else {
                        cleanupAndResolve({ isConfirmed: true, value: null });
                    }
                };
            }

            if (requireRemark) {
                const remarkInput = container.querySelector('#custom-modal-remark-input');
                if (remarkInput) {
                    remarkInput.oninput = () => {
                        const errEl = container.querySelector('#custom-modal-remark-error');
                        if (errEl) errEl.classList.add('hidden');
                    };
                    setTimeout(() => remarkInput.focus(), 50);
                }
            }
        });
    } else {
        // Classic Swal.fire Popup
        return Swal.fire({
            title: title,
            text: text,
            icon: icon,
            input: requireRemark ? "textarea" : undefined,
            inputPlaceholder: remarkPlaceholder,
            showCancelButton: true,
            showCloseButton: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
            confirmButtonText: confirmText,
            cancelButtonText: cancelText,
            customClass: {
                confirmButton: 'bg-yellow-500 text-white px-4 py-2 rounded me-2 cursor-pointer',
                cancelButton: 'bg-red-500 text-white px-4 py-2 rounded cursor-pointer'
            },
            preConfirm: (remarks) => {
                if (requireRemark && !remarks) {
                    Swal.showValidationMessage("Remark is required");
                }
                return remarks;
            }
        }).then((result) => {
            return {
                isConfirmed: Boolean(result.isConfirmed),
                value: result.isConfirmed ? (result.value || null) : null
            };
        });
    }
};
